import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';

@Injectable()
export class ErrorsInterceptor implements HttpInterceptor {

    private isHandlingAuthError = false;
    private translate: TranslateService;

    constructor(
        private router: Router,
        private authService: AuthService,
        protected toastrService: ToastrService,
        private injector: Injector
    ) { }

    private getTranslateService(): TranslateService {
        if (!this.translate) {
            this.translate = this.injector.get(TranslateService);
        }
        return this.translate;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(catchError(err => {

            if (err.status === 0) {

                const isCancelled = this.isRequestCancelled(err);

                if (isCancelled) {
                    return throwError(() => err);
                }

                let networkErrorMessage = this.getTranslateService().instant('Erro de conexão. Verifique sua internet.');

                if (!navigator.onLine) {
                    networkErrorMessage = this.getTranslateService().instant('Não conectado à internet.');
                } else if (err.message && err.message.includes('timeout')) {
                    networkErrorMessage = this.getTranslateService().instant('Tempo de conexão esgotado. Tente novamente.');
                }

                const networkError = new Error(networkErrorMessage);
                (networkError as any).status = 0;
                (networkError as any).originalError = err;
                (networkError as any).isNetworkError = true;

                return throwError(() => networkError);
            }

            if (err.status === 401 && err.error && err.error.message && err.error.message === 'unauthorized-signature-required') {
                if (!this.isHandlingAuthError) {
                    this.isHandlingAuthError = true;
                    this.toastrService.error(this.getTranslateService().instant('Recurso exclusivo para assinantes!'));
                    this.router.navigate(['/payments/sign']);
                    setTimeout(() => this.isHandlingAuthError = false, 3000);
                }
                return;
            } else if (err.status === 401) {
                if (!this.isHandlingAuthError) {
                    this.isHandlingAuthError = true;
                    this.toastrService.error(this.getTranslateService().instant('Necessário estar logado'));
                    this.authService.removeStorageCurrentUser();
                    this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });
                    setTimeout(() => this.isHandlingAuthError = false, 3000);
                }
            }

            let errorMessage = err.statusText ? err.statusText : this.getTranslateService().instant('Falha na comunicação com o servidor. Por favor, tente mais tarde.');

            if (err.error) {
                if (err.error.messages) {
                    errorMessage = err.error.messages;
                }
                if (err.error.message) {
                    errorMessage = err.error.message;
                }
            }

            if (errorMessage === 'Unknown Error' && err.message) {
                console.warn('Erro HTTP desconhecido:', err.message, err);
                if (!navigator.onLine) {
                    errorMessage = this.getTranslateService().instant('Não conectado à internet.');
                } else if (err.message.startsWith('Http failure response')) {
                    errorMessage = err.message; // Mantém a mensagem original do erro HTTP
                }
            }

            const error = new Error(errorMessage);
            (error as any).status = err.status;
            (error as any).originalError = err;
            if (err.error?.validation_errors) {
                (error as any).validation_errors = err.error.validation_errors;
            }
            if (err.error?.original_message) {
                (error as any).original_message = err.error.original_message;
            }

            return throwError(() => error);
        }));
    }

    private isRequestCancelled(err: any): boolean {

        if (err.name === 'HttpErrorResponse') {

            const errorMessage = (err.message || '').toLowerCase();
            const errorName = (err.name || '').toLowerCase();

            const isCancelledPattern =
                errorMessage.includes('canceled') ||
                errorMessage.includes('aborted') ||
                errorMessage.includes('cancelled') ||
                errorName.includes('abort');

            if (navigator.onLine && isCancelledPattern) {
                return true;
            }

            if (navigator.onLine &&
                !err.error &&
                !errorMessage.includes('network') &&
                !errorMessage.includes('timeout') &&
                !errorMessage.includes('failed')) {
                return true;
            }
        }

        return false;
    }
}
